import { COMPANY } from '@/shared/company';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { canAccessCoportalRoute } from '@/auth/coportalPages';
import {
  DEFAULT_HOT_PLATE_MODULE,
  LAST_MODULE_KEY,
  MY_NOTES_PATH,
  hotPlatePathForModule,
  hotPlateRequiredTile,
  isModuleSlug,
} from './moduleNav';
import { currentRegistries } from '@/shared/modules/store';
import { isNavItemVisible } from './navVisibility';
import {
  SERVER_MIN_LENGTH,
  asBookingRef,
  fetchOmniSearch,
  flatten,
  fold,
  mergeResults,
  rankPages,
  refShortcut,
  type IndexedPage,
  type ResultItem,
  type ServerHit,
  type ServerResult,
} from './omniSearch';
import { IconSearch, IconChevronRight } from '@/shared/icons';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { moduleAccent, type ModuleSlug } from '@/styles/tokens';

/**
 * Global search overlay — Cmd+K (or Ctrl+K on Windows/Linux). "Search
 * everything I can open": booking refs, guests, attachments, suppliers and
 * hotels, agents, issues, welcome meetings, and every page / module.
 *
 *   1. PAGES — built client-side from `MODULE_NAV` PER USER, through the same
 *      `isNavItemVisible` the module rail uses (tiles, item-level tiles,
 *      HR ▸ Appraisals, and the CS/OPS Portal bare-slug page grants). The
 *      two personal surfaces (My Notes, Hot Plate) left `MODULE_NAV` for the
 *      top bar in 2026-07-29 and are appended by hand. The index must never
 *      offer a row whose route answers with /forbidden.
 *
 *   2. RECORDS — `GET /api/search` (OmniSearchController) fans out to each
 *      source the user holds the page for, with logs data_filters /
 *      visible_columns applied server-side. See `omniSearch.ts` for the shared
 *      score scale that orders the groups (an exact booking ref first).
 *
 * Layout: 620 px centred dialog, one search field, results grouped under
 * section kickers. ARIA combobox → listbox: the input keeps focus, the arrow
 * keys move `aria-activedescendant`, Enter opens, ⇧Enter opens a file's
 * library page, ⌘/Ctrl-Enter opens in a new tab, Esc / backdrop closes.
 *
 * No command-palette library — the BQB signature bans them. Chrome comes from
 * tokens.css; the panel is the opaque `--bg-elev` surface (overlay rule).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Module you are currently in, if any. Two jobs: it picks WHICH Hot Plate
   * board gets indexed (there is only ever one row — see
   * {@link buildStaticPages}), and it floats that module's other pages up
   * the ranking so "issues" from CS/OPS Portal doesn't open Lemax's tracker.
   */
  activeModule?: ModuleSlug | null;
}

/** Debounce before the server is asked. */
const DEBOUNCE_MS = 180;

/** Human names for the server's source keys (partial-failure note). */
const SOURCE_LABEL: Record<string, string> = {
  bookings: 'bookings',
  guests: 'guest contacts',
  // A getter: the key agent is the runtime company profile's (Admin ▸ Company).
  get kuoni() {
    return `${COMPANY.keyAgent} contacts`;
  },
  attachments: 'attachments',
  suppliers: 'suppliers',
  agents: 'agents',
  issues: 'issues',
  meetings: 'welcome meetings',
};

/** The Topbar owns this key; the launcher only reads it. */
function readLastModule(): ModuleSlug | null {
  try {
    const raw = window.sessionStorage.getItem(LAST_MODULE_KEY);
    return isModuleSlug(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Build the page index for ONE user.
 *
 * Two things this must get right, both learned the hard way:
 *
 * 1. GATING. The index used to be a module-level constant, which meant it
 *    could not see the signed-in user at all — so it offered `/hr/hot-plate`,
 *    `/finance/…`, `/it/…` to everyone, and every one of those rows lands on
 *    `<RequireTile>`'s `<Navigate to="/forbidden">`. Visibility now goes
 *    through `isNavItemVisible` — the rail's own predicate plus the module
 *    tile (`MODULE_TILE`, the map `RequireTile` checks) — so a CS/OPS Portal
 *    page the admin took away is no longer offered either.
 *
 * 2. BUDGET. Hot Plate was indexed once per module — eleven rows all
 *    labelled "Hot Plate". `rankPages` caps at 8 and tie-breaks on the label,
 *    which is a no-op across eleven identical strings, so insertion order won
 *    and typing "hot" evicted Hotline Schedule and Hotline View from the
 *    results entirely. There is only ever ONE board a user means: the one the
 *    top bar points at. We resolve it the same way the Topbar does (current
 *    module → remembered module → CS/OPS Portal) and index that single row.
 */
export function buildStaticPages(
  canSeeTile: (slug: string) => boolean,
  user: Parameters<typeof isNavItemVisible>[2],
  hotPlateMod: ModuleSlug | null,
): IndexedPage[] {
  const out: IndexedPage[] = [];
  // The tenant's rail and labels: a switched-off module or page is never
  // indexed, and the tags read the tenant's names (Admin ▸ Modules).
  const { nav, labels } = currentRegistries();

  for (const [modKey, groups] of Object.entries(nav)) {
    const mod = modKey as ModuleSlug;
    for (const group of groups) {
      for (const item of group.items) {
        if (!isNavItemVisible(mod, item, user, canSeeTile, { checkModuleTile: true })) continue;
        out.push({
          to: item.to,
          label: item.label,
          module: mod,
          moduleLabel: labels[mod],
          accent: moduleAccent[mod],
          haystack: fold(`${item.label} ${labels[mod]} ${group.heading ?? ''}`),
        });
      }
    }
  }

  // ── Personal surfaces (2026-07-29 hoist, re-indexed 2026-07-31) ─────
  out.push({
    to: MY_NOTES_PATH,
    label: 'My Notes',
    // Module-less: one /api/notes for the whole hub, ungated by design (its
    // route carries no RequireTile). It borrows a slug for typing only; the
    // accent is the Amassa brand and the tag reads "Amassa".
    module: 'preferences',
    moduleLabel: 'Amassa',
    accent: 'var(--brand)',
    haystack: 'my notes sticky notepad personal amassa scratch',
  });

  const boardPath = hotPlateMod ? hotPlatePathForModule(hotPlateMod) : null;
  if (hotPlateMod && boardPath && canSeeTile(hotPlateRequiredTile(hotPlateMod))) {
    out.push({
      to: boardPath,
      label: 'Hot Plate',
      module: hotPlateMod,
      moduleLabel: labels[hotPlateMod],
      accent: moduleAccent[hotPlateMod],
      haystack: fold(`hot plate board kanban team tasks ${labels[hotPlateMod]}`),
    });
  }

  return out;
}

type ServerState = 'idle' | 'loading' | 'ready' | 'unavailable';

// ── Component ──────────────────────────────────────────────────────

export function SearchOverlay({ open, onClose, activeModule }: Props) {
  const navigate = useNavigate();
  const { user, canSeeTile } = useAuth();
  const reducedMotion = usePrefersReducedMotion();
  const [q, setQ] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [server, setServer] = useState<ServerResult | null>(null);
  const [serverState, setServerState] = useState<ServerState>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const optionId = (key: string) => `${listId}-opt-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`;

  // Reset on open / close.
  useEffect(() => {
    if (open) {
      setQ('');
      setActiveKey(null);
      setServer(null);
      setServerState('idle');
      // Focus after mount.
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Debounced server fetch. Aborted on every keystroke, so a slow answer to
  // an old query can never overwrite the current one.
  const trimmed = q.trim();
  useEffect(() => {
    if (!open || trimmed.length < SERVER_MIN_LENGTH) {
      setServer(null);
      setServerState('idle');
      return undefined;
    }
    const ctrl = new AbortController();
    setServer(null);
    setServerState('loading');
    const t = window.setTimeout(() => {
      void fetchOmniSearch(trimmed, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setServer(res);
          // Success — even zero hits is a valid "no matches", not a fault.
          setServerState('ready');
        })
        .catch((err) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          // Real backend/network failure — page matches still work.
          setServer(null);
          setServerState('unavailable');
        });
    }, DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [open, trimmed]);

  // Rebuilt per user, not per keystroke: `open` is in the deps so the
  // remembered module (sessionStorage, written by the Topbar) is re-read
  // each time the launcher is summoned rather than frozen at mount.
  const staticPages = useMemo(
    () =>
      buildStaticPages(
        canSeeTile,
        user,
        activeModule ?? readLastModule() ?? DEFAULT_HOT_PLATE_MODULE,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSeeTile, user, activeModule, open],
  );

  const canOpenLogs = canSeeTile('cs-portal') && canAccessCoportalRoute('logs-all', user);

  const groups = useMemo(() => {
    const pages = rankPages(staticPages, q, 8, activeModule);
    const ref = asBookingRef(q);
    // A module the user reaches only through Res Tools / Contracting: the
    // server picks the CS/OPS Portal route, the tiles decide here.
    const resolveTo = (h: ServerHit): string => {
      if (canSeeTile('cs-portal')) return h.to;
      if (h.kind === 'attachment' && canSeeTile('res-tools'))
        return h.to.replace(/^\/coportal\/attachments/, '/restools/attachments');
      if (h.kind === 'supplier' && canSeeTile('contracting')) return '/contracting/suppliers';
      return h.to;
    };
    return mergeResults({
      pages,
      server: server?.groups ?? [],
      shortcut: ref && canOpenLogs ? refShortcut(ref) : null,
      resolveTo,
    });
  }, [staticPages, q, activeModule, server, canOpenLogs, canSeeTile]);

  const flat = useMemo(() => flatten(groups), [groups]);
  const activeIndex = Math.max(0, flat.findIndex((i) => i.key === activeKey));
  const active: ResultItem | undefined = flat[activeIndex];

  // Keep the active row in view as the arrows walk past the fold.
  useEffect(() => {
    if (!active) return;
    const el = document.getElementById(optionId(active.key));
    el?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.key]);

  if (!open) return null;

  const openItem = (item: ResultItem, how: 'default' | 'library' | 'newTab' = 'default') => {
    if (how === 'newTab') {
      window.open(item.href ?? item.to, '_blank', 'noopener');
      onClose();
      return;
    }
    if (item.href && how === 'default') {
      // A file: open it where the attachments page's own "Open" link does.
      window.open(item.href, '_blank', 'noopener');
      onClose();
      return;
    }
    navigate(item.to);
    onClose();
  };

  const move = (delta: number) => {
    if (!flat.length) return;
    const next = Math.min(Math.max(activeIndex + delta, 0), flat.length - 1);
    const item = flat[next];
    if (item) setActiveKey(item.key);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'PageDown' || e.key === 'PageUp') {
      // Jump to the first row of the next / previous group.
      e.preventDefault();
      if (!active) return;
      const gi = groups.findIndex((g) => g.items.some((i) => i.key === active.key));
      const target = groups[gi + (e.key === 'PageDown' ? 1 : -1)];
      const first = target?.items[0];
      if (first) setActiveKey(first.key);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!active) return;
      openItem(active, e.metaKey || e.ctrlKey ? 'newTab' : e.shiftKey ? 'library' : 'default');
    }
  };

  const failed = server?.failed ?? [];
  const noResults =
    trimmed !== '' && flat.length === 0 && serverState !== 'loading';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
      onClick={onClose}
      className="motion-fade"
      style={overlayStyle}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="motion-pop hub-overlay"
        style={dialogStyle}
      >
        <div style={inputRowStyle}>
          <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
            <IconSearch size={18} />
          </span>
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-label="Search Amassa"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active ? optionId(active.key) : undefined}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveKey(null);
            }}
            onKeyDown={onKeyDown}
            placeholder="booking ref, guest, file, supplier, hotel, agent, page…"
            style={inputStyle}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="go"
          />
          <span style={kbdHint}>Esc</span>
        </div>

        <div style={resultsScroll}>
          {trimmed === '' ? (
            <EmptyHint />
          ) : (
            <div id={listId} role="listbox" aria-label="Search results">
              {groups.map((group) => (
                <div key={group.key} role="group" aria-label={group.label}>
                  <GroupHeader>{group.label}</GroupHeader>
                  {group.items.map((item) => (
                    <ResultRow
                      key={item.key}
                      id={optionId(item.key)}
                      item={item}
                      active={active?.key === item.key}
                      reducedMotion={reducedMotion}
                      onSelect={(how) => openItem(item, how)}
                      onHover={() => setActiveKey(item.key)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}

          {trimmed !== '' && serverState === 'loading' && (
            <div style={mutedRow} aria-hidden>
              searching bookings, guests, files and directories.
            </div>
          )}
          {serverState === 'unavailable' && (
            <div style={mutedRow}>
              record search is unavailable right now. page matches still work.
            </div>
          )}
          {serverState === 'ready' && failed.length > 0 && (
            <div style={mutedRow}>
              {failed.map((f) => SOURCE_LABEL[f] ?? f).join(', ')} did not answer in time.
            </div>
          )}
          {noResults && serverState !== 'unavailable' && (
            <div style={mutedRow}>
              {trimmed.length < SERVER_MIN_LENGTH
                ? 'keep typing to search records.'
                : <>no matches for &ldquo;{trimmed}&rdquo;.</>}
            </div>
          )}
        </div>

        <div className="sr-only" aria-live="polite">
          {serverState === 'loading'
            ? ''
            : trimmed
              ? `${flat.length} result${flat.length === 1 ? '' : 's'}`
              : ''}
        </div>

        <div style={footerStyle}>
          <span style={kbdHint}>↑↓</span>
          <span style={footerHelp}>navigate</span>
          <span style={kbdHint}>↵</span>
          <span style={footerHelp}>{active?.href ? 'open file' : 'open'}</span>
          {active?.href && (
            <>
              <span style={kbdHint}>⇧↵</span>
              <span style={footerHelp}>in library</span>
            </>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ ...footerHelp, ...footerCount }}>
            {staticPages.length} pages
            {server?.searched.length ? ` · ${server.searched.length} sources` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function EmptyHint() {
  return (
    <div style={mutedRow}>
      type a booking ref, guest name, file, supplier or hotel, agent, or any
      page you can open.
    </div>
  );
}

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      aria-hidden
      style={{
        fontFamily: 'var(--hm-mono)',
        fontSize: 9,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--text-dim)',
        fontWeight: 700,
        padding: '14px 18px 6px',
      }}
    >
      {children}
    </div>
  );
}

function ResultRow({
  id,
  item,
  active,
  reducedMotion,
  onSelect,
  onHover,
}: {
  id: string;
  item: ResultItem;
  active: boolean;
  reducedMotion: boolean;
  onSelect: (how: 'default' | 'library' | 'newTab') => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      data-kind={item.kind}
      // The input keeps focus (combobox pattern); a pointer press must not
      // steal it, or the arrow keys stop working after a click-and-drag.
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) =>
        onSelect(e.metaKey || e.ctrlKey ? 'newTab' : e.shiftKey ? 'library' : 'default')
      }
      onMouseEnter={onHover}
      style={{
        ...rowStyle,
        transition: reducedMotion ? 'none' : rowStyle.transition,
        background: active
          ? `color-mix(in oklab, ${item.accent} 12%, transparent)`
          : 'transparent',
        boxShadow: active ? `inset 2px 0 0 ${item.accent}` : 'none',
      }}
    >
      <span style={{ ...tagStyle, color: item.accent }}>{item.tag}</span>
      <span style={textCol}>
        <span style={titleStyle}>{item.title}</span>
        {item.subtitle && <span style={subStyle}>{item.subtitle}</span>}
      </span>
      <span
        aria-hidden
        style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', fontSize: 12 }}
      >
        {item.href ? '↗' : <IconChevronRight size={14} />}
      </span>
    </button>
  );
}

// ── Styles ─────────────────────────────────────────────────────────

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--q-scrim, color-mix(in oklab, var(--bg) 70%, rgba(0,0,0,0.55)))',
  backdropFilter: 'var(--q-none, blur(8px))',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  // Phones: sit near the top so the on-screen keyboard leaves room for rows.
  paddingTop: 'clamp(12px, 12vh, 120px)',
  zIndex: 120,
};

const dialogStyle: CSSProperties = {
  width: 'min(620px, 100% - 32px)',
  background: 'var(--bg-elev)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--hm-radius-lg)',
  boxShadow:
    'var(--q-edge-pop, 0 24px 48px -16px rgba(0,0,0,0.45), 0 0 0 1px color-mix(in oklab, var(--c-cs) 16%, transparent))',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'min(70vh, 100dvh - 32px)',
};

const inputRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 18px',
  borderBottom: '1px solid var(--border)',
};

const inputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontFamily: 'var(--hm-font)',
  // 16px: anything smaller makes iOS Safari zoom the page on focus.
  fontSize: 16,
  letterSpacing: '-0.005em',
  color: 'var(--text)',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
};

const resultsScroll: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '4px 0 8px',
};

const rowStyle: CSSProperties = {
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 18px',
  minHeight: 36,
  cursor: 'pointer',
  transition: 'background 120ms ease',
  width: '100%',
  boxSizing: 'border-box',
};

const tagStyle: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  fontWeight: 600,
  width: 84,
  flexShrink: 0,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const textCol: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const titleStyle: CSSProperties = {
  color: 'var(--text)',
  fontSize: 13,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const subStyle: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 10.5,
  color: 'var(--text-muted)',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mutedRow: CSSProperties = {
  padding: '14px 18px',
  fontFamily: 'var(--hm-mono)',
  fontSize: 11,
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 18px',
  borderTop: '1px solid var(--border)',
  background: 'var(--bg-card)',
};

const footerHelp: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 10.5,
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
};

const footerCount: CSSProperties = {
  whiteSpace: 'nowrap',
};

const kbdHint: CSSProperties = {
  display: 'inline-block',
  padding: '2px 6px',
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--q-r-xs, 4px)',
  fontFamily: 'var(--hm-mono)',
  fontSize: 10,
  letterSpacing: '0.08em',
  color: 'var(--text-muted)',
};
